import cn from "classnames";
import React, { useCallback, useMemo, useState } from "react";
import CommentInput from "./CommentInput";
import moment from "moment";
import { useIntl } from "react-intl";
import { DELETE_CONTACT_COMMENT_MODAL } from "../../../../constants/modal.constant";
import AreYouSure from "../../AreYouSure";
import { useEventCore } from "../../../../cores/event";
import { useGlobalCore } from "../../../../cores/globals";
import { useMeCore } from "../../../../cores/me";
import { CommentEvent } from "../../../../cores/event/interface/api/get-event";
import { Position } from "../../../../cores/globals/interfaces/globalEntity";
import Avatar from "../../Avatar";
import { UserStatus } from "../../../../cores/user/constants/user-status";
import { getFirstLetters } from "../../../utils/stringUtils";

interface Props {
  comment: CommentEvent;
}

export default function CommentCard(props: Props) {
  const intl = useIntl();
  const { comment } = props;
  const { user, company } = useMeCore();
  const { deleteComment, putComment, event } = useEventCore();
  const { turnOnModal, turnOffModal } = useGlobalCore();
  const [editComment, setEditComment] = useState(false);

  const toggleEditComment = useCallback(() => {
    setEditComment(!editComment);
  }, [editComment]);

  const onDelete = useCallback(() => {
    turnOnModal(
      DELETE_CONTACT_COMMENT_MODAL,
      <AreYouSure
        message={intl.formatMessage({ id: "app.activity.deleteComment" })}
        onSubmit={() => {
          deleteComment(
            company?.id!,
            event?.id!,
            comment.id
          );
          turnOffModal(DELETE_CONTACT_COMMENT_MODAL);
        }}
        onClose={() => turnOffModal(DELETE_CONTACT_COMMENT_MODAL)}
      />,
      Position.LEFT
    );
  }, [turnOnModal, turnOffModal, company, comment, event, deleteComment, intl]);

  const onUpdateCommentSubmit = (text: string) => {
    console.log(comment)
    putComment(
      company?.id!,
      event?.id!,
      comment.id,
      text
    );
    setEditComment(false);
  };

  const formatedComment = useMemo(
    () => ({
      __html: comment.text
        .replaceAll(/(?=@@@B\[)(.*?)(?=\)E@@@)/g, (value) => {
          const data = value.split("]@M@(");
          return `<span class="font-weight-bold pointer tagged ${
            +user?.id! === +data[1] ? "text-primary" : ""
          }">@${data[0]}</span>`;
        })
        .replace(/@@@B\[/g, "")
        .replace(/\)E@@@/g, ""),
    }),
    [comment.text, user]
  );

  const createdAt = useMemo(() =>
    moment(comment.createdAt).format("DD/MM/YYYY HH:mm a")
  , [comment.createdAt]);

  const updatedAt = useMemo(() =>
    moment(comment.updatedAt).format("DD/MM/YYYY HH:mm a")
  , [comment.updatedAt]);

  return (
    <div className="d-flex mt-3 mb-4 w-100">
      <Avatar
        className="text-dark-50"
        active={comment.user.status === UserStatus.ACTIVE.id}
        label={getFirstLetters(`${comment.user.lastName} ${comment.user.firstName}`)}
        image={comment.user.avatarUrl}
      />
      <div className="d-flex flex-column align-items-start ml-2 w-100">
        <div>
          <span className={cn("font-weight-bold mr-2", {
            "text-line-through": comment.user.status !== UserStatus.ACTIVE.id
          })}>
            {`${comment.user.firstName} ${comment.user.lastName}`}
          </span>
          <span className="text-muted">
            {createdAt}
            {createdAt !== updatedAt && " (Editado)"}
          </span>
        </div>
        {!editComment ? (
          <div className="p-2 border rounded">
            <div
              className="pre-wrap"
              dangerouslySetInnerHTML={formatedComment}
            />
          </div>
        ) : (
          <div className="p-2 w-100">
            <CommentInput
              onSubmit={onUpdateCommentSubmit}
              comment={comment.text}
              onClose={toggleEditComment}
            />
          </div>
        )}
        {comment.user.id === user?.id! && (
          <div className="mt-2">
            <span
              className="mr-2 text-underline pointer"
              onClick={toggleEditComment}
            >
              {intl.formatMessage({ id: "app.activity.modify" })}
            </span>
            <span className="text-underline pointer" onClick={onDelete}>
              {intl.formatMessage({ id: "app.activity.delete" })}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
